import React, { useEffect, useState } from 'react';
import {
  UserOutlined,
  LoginOutlined,
  MenuOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import ConfirmationModal from '../components/Modal/ConfirmationModal';
import UserAvatar from '../components/UserAvatar';
import { useAuth } from '../contexts/AuthProvider';
import { getSessionDisplayEmail, getSessionDisplayName } from '../utils/sessionDisplayName';
import { useHeader } from '../contexts/HeaderContext';
import { useSidebar } from '../contexts/SidebarContext';
import ThemeToggle from '../components/ThemeToggle';
import AdminBrandLogo from '../components/AdminBrandLogo';
import RouteCommandPalette from './RouteCommandPalette';
import HeaderNotifications from './HeaderNotifications';
import WorkspaceSwitcher from './WorkspaceSwitcher';

function resolveAvatarUrl(
  profileImage: string | null | undefined,
  auth0Picture: string | null | undefined,
): string | null {
  const candidates = [profileImage, auth0Picture];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
}

const Header: React.FC = () => {
  const { user } = useAuth();
  const { user: auth0User } = useAuth0();
  const { headerContent } = useHeader();
  const { toggleMobileOpen } = useSidebar();
  const navigate = useNavigate();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const displayName = getSessionDisplayName(auth0User, user);
  const displayEmail = getSessionDisplayEmail(auth0User, user);
  const avatarUrl = resolveAvatarUrl(user?.profile_image, auth0User?.picture);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <header className="relative flex h-14 items-center gap-2 border-b border-border bg-background-secondary px-2 sm:gap-2.5 sm:px-3 md:h-[4.5rem] md:gap-0 md:px-0 md:pr-4">
        <button
          type="button"
          onClick={toggleMobileOpen}
          className="flex-shrink-0 rounded-lg p-2 text-foreground-secondary transition-colors hover:bg-background-tertiary hover:text-foreground md:hidden"
          aria-label="Open navigation"
        >
          <MenuOutlined style={{ fontSize: 18 }} />
        </button>

        {/* Brand column = sidebar width on md+; logo fills nearly the full header height */}
        <div className="flex h-full min-w-0 flex-shrink items-center md:w-[260px] md:flex-shrink-0 md:px-1.5">
          <AdminBrandLogo className="h-12 w-auto max-w-[min(100%,14rem)] object-contain object-left sm:max-w-[16rem] md:h-16 md:max-w-full" />
        </div>

        {/* Short rule only — aligns with sidebar border-r, does not span full height (no grid line) */}
        <div
          className="hidden h-5 w-px flex-shrink-0 self-center bg-border lg:block"
          aria-hidden
        />

        <div className="hidden flex-shrink-0 items-center pl-3 md:flex">
          <WorkspaceSwitcher variant="header" />
        </div>

        {/* Search field stays until extreme narrow; only then collapses to icon */}
        <div className="mx-1 flex min-w-0 flex-1 justify-center max-[380px]:hidden">
          <button
            type="button"
            onClick={() => setIsSearchOpen(true)}
            className="flex h-9 w-full min-w-0 max-w-md items-center gap-2 rounded-xl border border-border bg-background px-2.5 text-left transition-colors hover:border-slate-300 hover:bg-background-tertiary/60 dark:hover:border-slate-600 sm:px-3 lg:gap-2.5 lg:px-3.5"
          >
            <SearchOutlined className="flex-shrink-0 text-foreground-muted" />
            <span className="min-w-0 flex-1 overflow-hidden whitespace-nowrap text-[13px] text-foreground-muted">
              Search routes, pages, workspaces…
            </span>
            <kbd className="hidden flex-shrink-0 rounded-md border border-border bg-background-secondary px-1.5 py-0.5 text-[10px] font-semibold text-foreground-muted lg:inline">
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Right utilities — never shrink */}
        <div className="header-content z-10 ml-auto flex flex-shrink-0 animate-fade-in items-center gap-1 sm:gap-1.5 md:gap-2">
          <button
            type="button"
            onClick={() => setIsSearchOpen(true)}
            className="hidden h-9 w-9 items-center justify-center rounded-xl bg-background text-foreground-muted ring-1 ring-border transition-colors hover:bg-background-tertiary hover:text-foreground max-[380px]:flex"
            aria-label="Search routes"
          >
            <SearchOutlined />
          </button>
          {headerContent.actions}
          <HeaderNotifications />
          <ThemeToggle />

          <div className="relative">
            <button
              type="button"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-1 rounded-full p-0.5 transition-colors hover:bg-background-tertiary sm:gap-1.5 sm:pr-1.5"
              aria-expanded={isUserMenuOpen}
              aria-haspopup="menu"
            >
              <span className="relative">
                <UserAvatar
                  profileImage={avatarUrl}
                  alt={displayName}
                  size="sm"
                  className="ring-2 ring-white dark:ring-slate-700"
                />
                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background-secondary bg-emerald-500" />
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="hidden h-3.5 w-3.5 flex-shrink-0 text-foreground-muted sm:block"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {isUserMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsUserMenuOpen(false)}
                />
                <div className="absolute right-0 top-full z-50 mt-2 w-[min(280px,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-border bg-background-secondary shadow-lg shadow-black/5">
                  <div className="flex items-center gap-3 bg-background px-4 py-3.5">
                    <span className="relative flex-shrink-0">
                      <UserAvatar
                        profileImage={avatarUrl}
                        alt={displayName}
                        size="md"
                        className="ring-2 ring-white dark:ring-slate-700"
                      />
                      <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background bg-emerald-500" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {displayName}
                      </p>
                      {displayEmail && (
                        <p className="truncate text-xs text-foreground-muted">
                          {displayEmail}
                        </p>
                      )}
                      {user?.roles?.[0] && (
                        <p className="mt-0.5 truncate text-[11px] text-foreground-muted">
                          {user.roles[0]}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-0.5 p-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/v2/manage-profile');
                        setIsUserMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-foreground-secondary transition-colors hover:bg-background hover:text-foreground"
                    >
                      <UserOutlined className="text-foreground-muted" />
                      Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsLogoutModalOpen(true);
                        setIsUserMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-foreground-secondary transition-colors hover:bg-background hover:text-foreground"
                    >
                      <LoginOutlined className="text-foreground-muted" />
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <RouteCommandPalette open={isSearchOpen} onOpenChange={setIsSearchOpen} />

      <ConfirmationModal
        ConfirmationHeading="Logout"
        ConfirmationParagraph="Are you sure you want to logout?"
        isOpen={isLogoutModalOpen}
        onOk={async () => {
          setIsLogoutModalOpen(false);
          navigate('/?logout=true');
        }}
        onCancel={() => setIsLogoutModalOpen(false)}
        icon={<LoginOutlined />}
      />
    </>
  );
};

export default Header;
